/**
 * YouTube Video Modal
 * Standalone modal system for YouTube video embeds
 * Portal pattern: Modals are moved to body level to escape parent stacking contexts
 */

(function () {
  'use strict';

  // Portal container - modals are moved here
  let portalContainer = null;

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Get or create portal container
    portalContainer = document.getElementById('custom-product-video-modal-portal');
    if (!portalContainer) {
      portalContainer = document.createElement('div');
      portalContainer.id = 'custom-product-video-modal-portal';
      document.body.appendChild(portalContainer);
    }

    // Initialize YouTube video modals
    initYouTubeModals();

    // Also initialize after a short delay to catch modals that might be added later
    setTimeout(function() {
      initYouTubeModals();
    }, 100);

    // Handle theme editor section loads
    document.addEventListener('shopify:section:load', function (event) {
      initYouTubeModals();
    });

    // Use MutationObserver to detect when modals are added to DOM (e.g., when accordions open)
    const observer = new MutationObserver(function(mutations) {
      let shouldReinit = false;
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            // Check if a YouTube modal or trigger was added
            if (node.matches && (
              node.matches('[data-youtube-video-modal]') ||
              node.querySelector && node.querySelector('[data-youtube-video-modal]') ||
              node.matches('[data-youtube-video-trigger]') ||
              node.querySelector && node.querySelector('[data-youtube-video-trigger]')
            )) {
              shouldReinit = true;
            }
          }
        });
      });
      if (shouldReinit) {
        initYouTubeModals();
      }
    });

    // Observe the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also use event delegation for trigger buttons to handle dynamically added buttons
    document.addEventListener('click', function(e) {
      const trigger = e.target.closest('[data-youtube-video-trigger]');
      if (trigger) {
        const modalId = trigger.getAttribute('data-modal-id');
        if (modalId) {
          // If button is not bound, initialize modals first
          if (!trigger.hasAttribute('data-bound')) {
            e.preventDefault();
            e.stopPropagation();
            initYouTubeModals();
            // Small delay to ensure modal is initialized, then trigger click
            setTimeout(function() {
              if (trigger.hasAttribute('data-bound')) {
                // Button is now bound, trigger the click event programmatically
                const clickEvent = new MouseEvent('click', {
                  bubbles: true,
                  cancelable: true,
                  view: window
                });
                trigger.dispatchEvent(clickEvent);
              } else {
                // If still not bound, try to find and open modal directly
                const modal = document.querySelector('[data-youtube-video-modal][data-modal-id="' + modalId + '"]');
                if (modal && !modal.hidden) {
                  // Modal exists and is visible, try to open it manually
                  modal.hidden = false;
                  document.documentElement.classList.add('has-video-modal-open');
                  document.documentElement.classList.add('is-modal-open');
                  document.body.classList.add('is-modal-open');
                }
              }
            }, 100);
            return false; // Prevent default action
          }
        }
      }
    }, true); // Use capture phase to catch events early

    // Listen for accordion/details toggle events to initialize modals when accordions open
    document.addEventListener('toggle', function(e) {
      const target = e.target;
      // Check if it's a details element or accordion-disclosure custom element
      const isDetails = target.matches && (target.matches('details') || target.matches('accordion-disclosure'));
      if (isDetails && target.open) {
        // Accordion opened - check if it contains YouTube modals or triggers
        const hasModal = target.querySelector('[data-youtube-video-modal]');
        const hasTrigger = target.querySelector('[data-youtube-video-trigger]');
        // Also check parent elements for triggers (since modal might be outside details)
        const parentTrigger = target.closest('[data-youtube-video-trigger]');
        const siblingModal = target.parentElement && target.parentElement.querySelector('[data-youtube-video-modal]');
        
        if (hasModal || hasTrigger || parentTrigger || siblingModal) {
          // Small delay to ensure DOM is fully updated
          setTimeout(function() {
            initYouTubeModals();
          }, 100);
        }
      }
    }, true); // Use capture phase to catch events early
    
    // Also listen for click events on accordion summaries to catch when they open
    document.addEventListener('click', function(e) {
      const summary = e.target.closest('summary');
      if (summary) {
        const details = summary.parentElement;
        if (details && (details.matches('details') || details.matches('accordion-disclosure'))) {
          // Check if this accordion has a video trigger
          setTimeout(function() {
            if (details.open) {
              const hasTrigger = details.querySelector('[data-youtube-video-trigger]');
              const siblingModal = details.parentElement && details.parentElement.querySelector('[data-youtube-video-modal]');
              if (hasTrigger || siblingModal) {
                initYouTubeModals();
              }
            }
          }, 150);
        }
      }
    }, true);
  }

  function initYouTubeModals() {
    // Get or create portal container
    if (!portalContainer) {
      portalContainer = document.getElementById('custom-product-video-modal-portal');
      if (!portalContainer) {
        portalContainer = document.createElement('div');
        portalContainer.id = 'custom-product-video-modal-portal';
        document.body.appendChild(portalContainer);
      }
    }

    // Find all YouTube video modals (including those that might have been initialized but not bound)
    const youtubeModals = document.querySelectorAll('[data-youtube-video-modal]');
    
    youtubeModals.forEach(function (modal) {
      // Skip if already initialized
      if (modal.hasAttribute('data-initialized')) {
        // But still check if buttons need to be bound
        const modalId = modal.getAttribute('data-modal-id');
        if (modalId) {
          const triggerButtons = document.querySelectorAll('[data-youtube-video-trigger][data-modal-id="' + modalId + '"]:not([data-bound])');
          if (triggerButtons.length > 0) {
            // Re-initialize this modal to bind new buttons
            modal.removeAttribute('data-initialized');
          } else {
            return; // Skip this modal, already initialized and all buttons bound
          }
        } else {
          return; // Skip this modal, no modal-id
        }
      }
      // Mark as initialized to prevent duplicate event binding
      modal.setAttribute('data-initialized', 'true');
      
      // Portal: Move modal to body level if not already there
      if (modal.parentElement !== portalContainer) {
        portalContainer.appendChild(modal);
      }

      const modalId = modal.getAttribute('data-modal-id');
      let videoId = modal.getAttribute('data-video-id');
      const videoUrl = modal.getAttribute('data-video-url');
      
      // Check if modal uses consent wrapper (new GDPR-compliant approach)
      const consentEmbed = modal.querySelector('[data-yt-consent-embed]');
      
      if (!modalId) {
        console.warn('No modal-id found for YouTube modal');
        return;
      }
      
      // If video ID is not set but video URL is, try to extract it
      if (!videoId && videoUrl) {
        // Extract YouTube video ID from URL
        let extractedId = null;
        if (videoUrl.includes('youtube.com/watch?v=')) {
          extractedId = videoUrl.split('v=')[1]?.split('&')[0]?.split('?')[0];
        } else if (videoUrl.includes('youtu.be/')) {
          extractedId = videoUrl.split('youtu.be/')[1]?.split('?')[0]?.split('&')[0];
        } else if (videoUrl.includes('youtube.com/embed/')) {
          extractedId = videoUrl.split('embed/')[1]?.split('?')[0]?.split('&')[0];
        } else if (videoUrl.length === 11) {
          extractedId = videoUrl;
        }
        
        if (extractedId) {
          videoId = extractedId;
          modal.setAttribute('data-video-id', videoId);
          
          // If using consent wrapper, update its data-video-id
          if (consentEmbed) {
            consentEmbed.setAttribute('data-video-id', videoId);
            consentEmbed.removeAttribute('data-video-url');
          }
        } else {
          console.warn('Could not extract YouTube video ID from URL:', videoUrl, 'for modal:', modalId);
          return;
        }
      }
      
      // For consent wrapper: video loading is handled by youtube-consent.js
      // Modal JS only handles open/close functionality
      if (consentEmbed) {
        // Consent system will handle video loading
        // Modal just needs to handle open/close
      } else {
        // Legacy iframe support (fallback for non-consent modals)
        const iframe = modal.querySelector('[data-youtube-video-iframe]');
        if (!iframe) {
          console.warn('No iframe or consent embed found for YouTube modal:', modalId);
          return;
        }
        
        if (!videoId) {
          console.warn('No video ID found for YouTube modal:', modalId);
          return;
        }
        
        // Legacy: Set iframe src (only if not using consent wrapper)
        if (!iframe.src || iframe.src === '' || iframe.hasAttribute('data-youtube-video-url')) {
          const origin = window.location.origin;
          iframe.src = 'https://www.youtube-nocookie.com/embed/' + videoId + '?playsinline=1&enablejsapi=1&rel=0&modestbranding=1&origin=' + encodeURIComponent(origin);
          iframe.removeAttribute('data-youtube-video-url');
        }
      }

      // Store reference to trigger for focus return
      let previousActiveElement = null;
      
      // Safari: save scroll position when opening, restore when closing (Safari resets scroll on overflow:hidden removal)
      let savedScrollY = 0;
      
      // Store previous inline styles for defensive override
      let headerPrevStyle = null;
      let buyboxPrevStyle = null;

      // Define open/close functions first (will be used by buttons)
      function openYouTubeModal() {
        // Safari: save scroll position before scroll lock (Safari resets it when overflow:hidden is removed)
        savedScrollY = window.scrollY || window.pageYOffset || 0;
        
        // Show modal
        modal.hidden = false;
        
        // Global state: mark modal as open
        document.documentElement.classList.add('has-video-modal-open');
        
        // Scroll lock: prevent background scrolling
        document.documentElement.classList.add('is-modal-open');
        document.body.classList.add('is-modal-open');
        
        // Defensive JS: Neutralize sticky elements by overriding inline styles
        const headerWrap = document.querySelector('#shopify-section-sections--25373473603851__header') || 
                           document.querySelector('header.shopify-section--header') ||
                           document.querySelector('.shopify-section--header');
        const buybox = document.querySelector('safe-sticky.product-info');
        
        // Store previous inline styles for restoration
        if (headerWrap) {
          headerPrevStyle = headerWrap.getAttribute('style');
          headerWrap.style.cssText += '; position: relative !important; top: auto !important; z-index: 0 !important; transform: none !important; filter: none !important; pointer-events: none !important;';
        }
        
        if (buybox) {
          buyboxPrevStyle = buybox.getAttribute('style');
          buybox.style.cssText += '; position: relative !important; top: auto !important; z-index: 0 !important; transform: none !important; filter: none !important; will-change: auto !important;';
        }
        
        // Accessibility: Focus close button
        const closeButton = modal.querySelector('[data-youtube-video-close]');
        if (closeButton) {
          setTimeout(function() {
            closeButton.focus();
          }, 100);
        }
      }

      function closeYouTubeModal() {
        // Hide modal
        modal.hidden = true;
        
        // Global state: remove modal open flag
        document.documentElement.classList.remove('has-video-modal-open');
        
        // Scroll unlock: restore background scrolling
        document.documentElement.classList.remove('is-modal-open');
        document.body.classList.remove('is-modal-open');
        
        // Defensive JS: Restore previous inline styles
        const headerWrap = document.querySelector('#shopify-section-sections--25373473603851__header') || 
                           document.querySelector('header.shopify-section--header') ||
                           document.querySelector('.shopify-section--header');
        const buybox = document.querySelector('safe-sticky.product-info');
        
        if (headerWrap) {
          if (headerPrevStyle === null) {
            headerWrap.removeAttribute('style');
          } else {
            headerWrap.setAttribute('style', headerPrevStyle);
          }
          headerPrevStyle = null;
        }
        
        if (buybox) {
          if (buyboxPrevStyle === null) {
            buybox.removeAttribute('style');
          } else {
            buybox.setAttribute('style', buyboxPrevStyle);
          }
          buyboxPrevStyle = null;
        }
        
        // Accessibility: Return focus to trigger element
        if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
          previousActiveElement.focus();
          previousActiveElement = null;
        }
        
        // Safari: restore scroll position (Safari resets scroll when overflow:hidden is removed)
        requestAnimationFrame(function() {
          window.scrollTo(0, savedScrollY);
        });
        
        // Stop YouTube video
        const consentEmbed = modal.querySelector('[data-yt-consent-embed]');
        if (consentEmbed) {
          // Consent wrapper: video is managed by youtube-consent.js
          // Video will stop when modal closes (iframe removed from DOM)
        } else {
          // Legacy iframe: stop by changing src
          const iframe = modal.querySelector('[data-youtube-video-iframe]');
          if (iframe) {
            const currentSrc = iframe.src;
            iframe.src = '';
            // Restore src for next time
            setTimeout(function() {
              iframe.src = currentSrc;
            }, 100);
          }
        }
      }

      // Store functions on modal for later use
      modal._openYouTubeModal = openYouTubeModal;
      modal._closeYouTubeModal = closeYouTubeModal;

      // Find trigger buttons for this modal (only those not already bound)
      const triggerButtons = document.querySelectorAll('[data-youtube-video-trigger][data-modal-id="' + modalId + '"]:not([data-bound])');
      
      // Bind buttons if found
      if (triggerButtons.length > 0) {
        triggerButtons.forEach(function (triggerButton) {
          // Mark as bound to prevent duplicate event listeners
          triggerButton.setAttribute('data-bound', 'true');
          
          triggerButton.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            previousActiveElement = document.activeElement;
            openYouTubeModal();
          });
        });
      }

      // Close modal handlers
      const closeButtons = modal.querySelectorAll('[data-youtube-video-close]');
      const closeButton = closeButtons[0]; // Store first close button for focus
      closeButtons.forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          closeYouTubeModal();
        });
      });

      // Close on overlay click (but not on dialog content)
      const overlay = modal.querySelector('.custom-product-video-modal__overlay');
      if (overlay) {
        overlay.addEventListener('click', function (e) {
          if (e.target === overlay) {
            closeYouTubeModal();
          }
        });
      }

      // Close on ESC key (only when modal is open)
      // Use a unique handler per modal to avoid conflicts
      const escapeHandler = function(e) {
        if (e.key === 'Escape' && !modal.hidden) {
          closeYouTubeModal();
        }
      };
      // Store handler on modal for cleanup if needed
      modal._escapeHandler = escapeHandler;
      document.addEventListener('keydown', escapeHandler);
    });
  }
})();
